import React, { useState } from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { precedents } from '../../../data/peerData';

const getSimilarityColor = (similarity: number): string => {
  if (similarity >= 80) return 'text-data-green';
  if (similarity >= 60) return 'text-data-amber';
  return 'text-gray-400';
};

interface PrecedentsPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const PrecedentsPanel: React.FC<PrecedentsPanelProps> = ({ isMinimized, onMinimize }) => {
  const [selectedPrecedent, setSelectedPrecedent] = useState(0);
  const selected = precedents[selectedPrecedent];

  return (
    <Panel title="HISTORICAL PRECEDENTS" subtitle="Reform Success Stories" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full gap-2">
        {/* Precedent Tabs */}
        <div className="flex gap-1">
          {precedents.map((p, i) => (
            <button
              key={p.country}
              onClick={() => setSelectedPrecedent(i)}
              className={`px-2 py-1 text-xs rounded transition-all ${
                i === selectedPrecedent
                  ? 'bg-data-green/20 text-data-green border border-data-green/30'
                  : 'bg-terminal-bg text-gray-400 hover:text-white'
              }`}
            >
              {p.country}
              <span className="ml-1 text-xs text-gray-500">{p.period.split('-')[0]}</span>
            </button>
          ))}
        </div>

        {/* Selected Precedent Detail */}
        <div className="flex-1 bg-terminal-bg rounded p-3">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-white font-bold text-sm">{selected.country}</h3>
              <div className="text-gray-400 text-xs">{selected.period}</div>
              <div className="text-data-green text-xs mt-1 font-medium">"{selected.catalyst}"</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">UZB SIMILARITY</div>
              <div className={`text-xl font-bold ${getSimilarityColor(selected.uzbSimilarity)}`}>
                {selected.uzbSimilarity}%
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="text-center">
              <div className="text-xs text-gray-500">GDP BEFORE</div>
              <div className="text-sm font-bold text-white">${selected.gdpBefore}B</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">GDP AFTER</div>
              <div className="text-sm font-bold text-data-green">${selected.gdpAfter}B</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">FDI MULTIPLE</div>
              <div className="text-sm font-bold text-data-amber">{selected.fdiMultiple}x</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">STOCK RETURN</div>
              <div className="text-sm font-bold text-data-green">{selected.stockReturn}</div>
            </div>
          </div>

          {/* Key Factors */}
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">Key Success Factors</div>
            <div className="grid grid-cols-2 gap-1">
              {selected.keyFactors.map((factor, i) => (
                <div key={i} className="flex items-center text-xs text-gray-300">
                  <span className="text-data-green mr-1">✓</span>
                  {factor}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1">Quick Comparison</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left py-0.5">Country</th>
                <th className="text-right">Period</th>
                <th className="text-right">GDP Growth</th>
                <th className="text-right">FDI ×</th>
                <th className="text-right">Returns</th>
                <th className="text-right">UZB Match</th>
              </tr>
            </thead>
            <tbody>
              {precedents.map((p, i) => (
                <tr
                  key={p.country}
                  className={`border-t border-terminal-border/50 ${i === selectedPrecedent ? 'bg-data-green/5' : ''}`}
                  onClick={() => setSelectedPrecedent(i)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="py-1 text-white font-medium">{p.country}</td>
                  <td className="py-1 text-gray-400 text-right">{p.period}</td>
                  <td className="py-1 text-data-green text-right">
                    +{(((p.gdpAfter - p.gdpBefore) / p.gdpBefore) * 100).toFixed(0)}%
                  </td>
                  <td className="py-1 text-data-amber text-right">{p.fdiMultiple}x</td>
                  <td className="py-1 text-data-green text-right">{p.stockReturn}</td>
                  <td className={`py-1 text-right font-medium ${getSimilarityColor(p.uzbSimilarity)}`}>
                    {p.uzbSimilarity}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* UZB Opportunity Note */}
        <div className="bg-data-green/10 border border-data-green/30 rounded p-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-data-green font-bold">UZB OPPORTUNITY:</span>
            <span className="text-gray-300">
              Following reform precedents, UZB has potential GDP growth to <span className="text-data-green font-bold">$180B+</span> by 2035
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2 border-t border-terminal-border flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default PrecedentsPanel;
